/**
 * Permission codes the Determination Simulator declares an interest in.
 *
 * The simulator is pure orchestration of existing endpoints, so it holds no
 * permission of its own: each step needs exactly the permission its endpoint's
 * route declares (verified against each handler, not inferred from the URL).
 * A step whose permission the user lacks is shown as **not checked** with that
 * reason — never as passed — and the run's verdict says it is incomplete.
 *
 * The screen is gated on business-unit read: the first step resolves the
 * business unit, and without it there is no chain to run.
 */

import { BUSINESS_UNIT_PERMISSIONS } from '@/modules/enterprise/business-units/permissions'
import { LEGAL_ENTITY_PERMISSIONS } from '@/modules/enterprise/legal-entities/permissions'
import { ORG_PERMISSIONS } from '@/modules/org/permissions'

export const SIMULATOR_PERMISSIONS = {
  /** `GET /foundation/business-units/:id` and `/resolve-client`. */
  read: BUSINESS_UNIT_PERMISSIONS.read,
  /** `GET /foundation/business-units/:id/security-scope`. */
  securityScope: BUSINESS_UNIT_PERMISSIONS.securityScope,
  /** `GET /opening-balances` and `/foundation/opening-balances/:id`. */
  openingBalanceRead: ORG_PERMISSIONS.openingBalanceRead,
  /** `GET /foundation/ledgers/resolve`. */
  ledgerRead: ORG_PERMISSIONS.ledgerRead,
  /** `GET /fiscal-periods` (the period step also reads `GET /foundation/ledgers` for the ledger's calendar, under `ledgerRead`). */
  fiscalRead: ORG_PERMISSIONS.fiscalRead,
  /** `GET /foundation/account-combinations/validate`. */
  combinationValidate: 'coa.combination.validate',
  /** `GET /foundation/charts-of-accounts` — the structure picker. */
  coaStructureRead: 'coa.structure.read',
  /** `POST /foundation/intercompany/simulate` — the intercompany step's check; simulate is a manage-level call. */
  intercompany: LEGAL_ENTITY_PERMISSIONS.intercompanyManage,
  /** `GET /foundation/intercompany-rules` — finding the rule to simulate. */
  intercompanyRead: LEGAL_ENTITY_PERMISSIONS.intercompanyRead,
  /** `GET /foundation/legal-entities` — the counterparty picker. */
  legalEntityRead: LEGAL_ENTITY_PERMISSIONS.read,
} as const
